import { motion } from "framer-motion";
import { useTranslation } from "@/i18n/LanguageContext";
import VangardHeader from "@/components/vangard/VangardHeader";
import VangardFooter from "@/components/vangard/VangardFooter";
import VMark from "@/components/vangard/VMark";
import Seo from "@/components/Seo";

const SECTIONS = [
  {
    title: { ru: 'Какую информацию мы собираем', uk: 'Яку інформацію ми збираємо', en: 'Information we collect' },
    body:  { ru: 'Мы собираем информацию, которую вы предоставляете нам напрямую: имя, email, телефон и любые другие данные, которыми вы решаете поделиться при использовании сервиса.', uk: 'Ми збираємо інформацію, яку ви надаєте нам напряму: імʼя, email, телефон та будь-які інші дані, якими ви вирішуєте поділитися.', en: 'We collect information you provide directly to us, including your name, email address, phone number, and any other information you choose to share when using our services or contacting us.' },
  },
  {
    title: { ru: 'Как мы её используем', uk: 'Як ми її використовуємо', en: 'How we use it' },
    body:  { ru: 'Для предоставления, поддержки и улучшения сервисов; обработки транзакций и связанных уведомлений; технических сообщений; ответов на ваши запросы; и коммуникации о сервисах, предложениях и событиях.', uk: 'Для надання, підтримки та покращення сервісів; обробки транзакцій та повідомлень; технічних повідомлень; відповідей на ваші запити; та комунікації про сервіси, пропозиції та події.', en: 'To provide, maintain and improve our services; to process transactions and send related information; to send technical notices and support messages; to respond to your comments, questions and requests; and to communicate with you about services, offers and events.' },
  },
  {
    title: { ru: 'Передача информации', uk: 'Передача інформації', en: 'Information sharing' },
    body:  { ru: 'Мы не продаём ваши персональные данные. Мы можем делиться информацией с провайдерами, которые работают от нашего имени под строгими обязательствами о конфиденциальности.', uk: 'Ми не продаємо ваші персональні дані. Ми можемо ділитися інформацією з провайдерами, які працюють від нашого імені під суворими зобов’язаннями конфіденційності.', en: 'We do not sell your personal information. We may share information with service providers who perform work on our behalf under strict confidentiality obligations.' },
  },
  {
    title: { ru: 'Безопасность данных', uk: 'Безпека даних', en: 'Data security' },
    body:  { ru: 'Мы принимаем разумные меры для защиты информации от потери, кражи, злоупотребления, несанкционированного доступа, раскрытия, изменения и уничтожения. Вся передача шифруется отраслевыми протоколами.', uk: 'Ми вживаємо розумних заходів для захисту інформації від втрати, крадіжки, зловживання, несанкціонованого доступу, розкриття, зміни та знищення. Усе передавання шифрується галузевими протоколами.', en: 'We take reasonable measures to protect information from loss, theft, misuse, unauthorised access, disclosure, alteration and destruction. All transmission is encrypted using industry-standard protocols.' },
  },
  {
    title: { ru: 'Ваши права', uk: 'Ваші права', en: 'Your rights' },
    body:  { ru: 'Вы можете получить доступ, обновить или удалить свои персональные данные в любое время, связавшись с нами. Также можно отписаться от промо-коммуникаций.', uk: 'Ви можете отримати доступ, оновити або видалити свої персональні дані у будь-який час, зв’язавшись з нами. Також можна відписатися від промо-комунікацій.', en: 'You may access, update or delete your personal information at any time by contacting us. You may also opt out of promotional communications by following the instructions in those messages.' },
  },
  {
    title: { ru: 'Контакт', uk: 'Контакт', en: 'Contact' },
    body:  { ru: 'По вопросам этой политики: privacy@vangardclub.com.', uk: 'Із питань цієї політики: privacy@vangardclub.com.', en: 'If you have any questions about this policy, contact us at privacy@vangardclub.com.' },
  },
];

export default function PrivacyPage() {
  const { lang, t } = useTranslation();
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="privacy-page">
      <Seo title={t("vc.seo.privacy.title")} description={t("vc.seo.privacy.desc")} />
      <VangardHeader />

      <section className="relative overflow-hidden pt-[92px]">
        <VMark position="top-right" size={620} opacity={0.05} />
        <div className="relative max-w-[900px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="text-[11px] tracking-[0.35em] uppercase text-neutral-400">{t("vc.legal.eyebrow")}</div>
            <h1 className="font-serif text-5xl sm:text-6xl mt-6 leading-[1.05]">
              {t("vc.legal.privacy-title")} <span className="italic text-neutral-300">{t("vc.legal.privacy-italic")}</span>
            </h1>
            <p className="mt-6 text-sm text-neutral-500 tracking-wide">{t("vc.legal.updated")}</p>
          </motion.div>

          <div className="mt-16 space-y-12">
            {SECTIONS.map((s, i) => (
              <motion.section
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: 0.03 * i }}
              >
                <div className="flex items-baseline gap-4">
                  <span className="font-serif text-neutral-600 text-xl w-8">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="font-serif text-2xl">{s.title[lang]}</h2>
                </div>
                <p className="mt-4 pl-12 text-neutral-400 font-light leading-relaxed">{s.body[lang]}</p>
              </motion.section>
            ))}
          </div>
        </div>
      </section>

      <VangardFooter />
    </div>
  );
}
